import ExpandOutlined from '@ant-design/icons/ExpandOutlined';
import PartitionOutlined from '@ant-design/icons/PartitionOutlined';
import ZoomInOutlined from '@ant-design/icons/ZoomInOutlined';
import ZoomOutOutlined from '@ant-design/icons/ZoomOutOutlined';
import { useReactFlow } from '@xyflow/react';
import Tooltip from 'antd/es/tooltip';
import React from 'react';
import { styled } from 'styled-components';

const ControlsContainer = styled.div`
  position: absolute;
  bottom: 40px;
  right: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 1000;
`;

const ControlButton = styled.button`
  width: 32px;
  height: 32px;
  border: 1px solid ${({ theme }) => theme.colorBorder};
  border-radius: 4px;
  background: ${({ theme }) => theme.colorBgContainer};
  color: ${({ theme }) => theme.colorText};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all ${({ theme }) => theme.motionDurationFast} ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  &:hover {
    background: ${({ theme }) => theme.colorBgTextHover};
    border-color: ${({ theme }) => theme.colorPrimary};
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;

    &:hover {
      background: ${({ theme }) => theme.colorBgContainer};
      border-color: ${({ theme }) => theme.colorBorder};
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      transform: none;
    }
  }

  .anticon {
    font-size: 14px;
  }
`;

export interface CanvasControlsProps {
  onAutoLayout?: () => void;
  disabled?: boolean;
}

export const CanvasControls: React.FC<CanvasControlsProps> = ({
  onAutoLayout,
  disabled = false,
}) => {
  const { zoomIn, zoomOut, fitView } = useReactFlow();

  const handleZoomIn = () => {
    void zoomIn({ duration: 200 });
  };

  const handleZoomOut = () => {
    void zoomOut({ duration: 200 });
  };

  const handleFitView = () => {
    void fitView({
      duration: 300,
      padding: 0.1, // 10% padding around nodes
      includeHiddenNodes: false,
    });
  };

  const handleAutoLayout = () => {
    if (onAutoLayout) {
      onAutoLayout();
    }
  };

  return (
    <ControlsContainer>
      <Tooltip title="Zoom In" placement="left">
        <ControlButton
          onClick={handleZoomIn}
          disabled={disabled}
          aria-label="Zoom in"
        >
          <ZoomInOutlined />
        </ControlButton>
      </Tooltip>

      <Tooltip title="Zoom Out" placement="left">
        <ControlButton
          onClick={handleZoomOut}
          disabled={disabled}
          aria-label="Zoom out"
        >
          <ZoomOutOutlined />
        </ControlButton>
      </Tooltip>

      <Tooltip title="Fit View (Show All Blocks)" placement="left">
        <ControlButton
          onClick={handleFitView}
          disabled={disabled}
          aria-label="Fit view to show all blocks"
        >
          <ExpandOutlined />
        </ControlButton>
      </Tooltip>

      <Tooltip title="Auto Layout" placement="left">
        <ControlButton
          onClick={handleAutoLayout}
          disabled={disabled}
          aria-label="Auto arrange blocks"
        >
          <PartitionOutlined />
        </ControlButton>
      </Tooltip>
    </ControlsContainer>
  );
};
